import { Grid } from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetMaintenanceAlertsQuery } from "../../api/maintenanceApi";
import {
  PtiComplianceListItemDto,
  useGetPtiComplianceQuery,
  useGetPtiComplianceSummaryQuery,
} from "../../api/ptiApi";
import { useGetShopRequestsQuery } from "../../api/shopsApi";
import { useGetActiveFaultEventsQuery } from "../../api/vehiclesApi";
import { useAppSelector } from "../../app/hooks";
import { MenuItemBase } from "../../components/layout/navMenu";
import {
  MaintenanceIcon,
  PtiIcon,
  StoreIcon,
  VehiclesIcon,
} from "../../components/common/pageIcons";
import { UserRoles } from "../../constants/roles";
import { hasSubscriptionAccess } from "../../constants/subscriptionLevels";
import { getPtiStatusLabel } from "../pti/ptiConstants";
import { getShopPreferenceLabel } from "../../utils/statusColors";
import HubGroupPanel, { HubPreviewRow } from "./components/HubGroupPanel";
import HubOverviewSection from "./components/HubOverviewSection";
import { formatShortDate, hasRole, hasShortcut } from "./hubUtils";

interface FleetHubOverviewProps {
  shortcuts: MenuItemBase[];
  subscriptionLevel: string | null;
}

const PTI_ROLES = [UserRoles.ADMIN, UserRoles.FLEET, UserRoles.SUPER_ADMIN];
const MAINTENANCE_ROLES = [UserRoles.ADMIN, UserRoles.FLEET];
const SHOP_REQUEST_ROLES = [
  UserRoles.ADMIN,
  UserRoles.DISPATCHER,
  UserRoles.FLEET,
  UserRoles.SUPER_ADMIN,
];

const MAINTENANCE_ALERT_LABELS: Record<string, string> = {
  OilChange: "Oil change",
  TuneUp: "Tune up",
  AirDryFilterRecommended: "Air dry filter",
  AirDryFilterRequired: "Air dry filter",
  Coolant: "Coolant",
  DpfFilter: "DPF filter",
  TireTreadDepth: "Tire tread",
  BrakePadCaution: "Brake pad",
  BrakePadReplace: "Brake pad",
};

const formatDue = (row: PtiComplianceListItemDto) => {
  if (row.complianceStatus === "Overdue") {
    return row.daysOverdue > 0 ? `${row.daysOverdue}d overdue` : "Overdue";
  }
  if (row.hoursUntilDue <= 24) {
    return `Due in ${row.hoursUntilDue}h`;
  }
  return `Due in ${Math.ceil(row.hoursUntilDue / 24)}d`;
};

const buildPtiHref = (row: PtiComplianceListItemDto) => {
  const params = new URLSearchParams({
    tab: "compliance",
    status: row.complianceStatus === "Overdue" ? "overdue" : "dueSoon",
    driver: String(row.driverUserId),
  });
  if (row.assetType === "Vehicle") {
    params.set("vehicle", String(row.assetId));
  } else {
    params.set("trailer", String(row.assetId));
  }
  if (row.lastPtiId) {
    params.set("inspection", String(row.lastPtiId));
  }
  return `/pti?${params.toString()}`;
};

const FleetHubOverview: React.FC<FleetHubOverviewProps> = ({
  shortcuts,
  subscriptionLevel,
}) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const role = user?.role;

  const canPti =
    hasShortcut(shortcuts, "/pti") &&
    hasRole(role, PTI_ROLES) &&
    hasSubscriptionAccess(subscriptionLevel, "Standard");
  const canMaintenance =
    hasShortcut(shortcuts, "/maintenance") && hasRole(role, MAINTENANCE_ROLES);
  const canFaults = hasShortcut(shortcuts, "/vehicles");
  const canShopRequests =
    hasShortcut(shortcuts, "/shops") && hasRole(role, SHOP_REQUEST_ROLES);

  const {
    data: ptiSummary,
    isLoading: ptiSummaryLoading,
    isError: ptiSummaryError,
  } = useGetPtiComplianceSummaryQuery({}, { skip: !canPti });
  const { data: ptiAttention, isLoading: ptiListLoading } =
    useGetPtiComplianceQuery(
      { pageNumber: 1, pageSize: 20, status: "needsAttention" },
      { skip: !canPti },
    );
  const {
    data: alerts = [],
    isLoading: alertsLoading,
    isError: alertsError,
  } = useGetMaintenanceAlertsQuery(undefined, { skip: !canMaintenance });
  const {
    data: faults = [],
    isLoading: faultsLoading,
    isError: faultsError,
  } = useGetActiveFaultEventsQuery(undefined, { skip: !canFaults });
  const {
    data: shopRequests = [],
    isLoading: shopsLoading,
    isError: shopsError,
  } = useGetShopRequestsQuery({ status: 0 }, { skip: !canShopRequests });

  const overdueCount = ptiSummaryError ? 0 : ptiSummary?.overdueCount ?? 0;
  const dueSoonCount = ptiSummaryError ? 0 : ptiSummary?.dueSoonCount ?? 0;
  const criticalAlerts = useMemo(
    () => alerts.filter((alert) => alert.severity === "Critical"),
    [alerts],
  );
  const warningAlerts = useMemo(
    () => alerts.filter((alert) => alert.severity !== "Critical"),
    [alerts],
  );

  const faultsByVehicle = useMemo(() => {
    const grouped = new Map<number, typeof faults>();
    for (const fault of faults) {
      const list = grouped.get(fault.vehicleId) ?? [];
      list.push(fault);
      grouped.set(fault.vehicleId, list);
    }
    return grouped;
  }, [faults]);

  const ptiRows = useMemo<HubPreviewRow[]>(() => {
    const items = [...(ptiAttention?.items ?? [])].sort((a, b) => {
      if (a.complianceStatus !== b.complianceStatus) {
        return a.complianceStatus === "Overdue" ? -1 : 1;
      }
      return b.daysOverdue - a.daysOverdue;
    });
    return items.map((row) => {
      const lastDate = formatShortDate(row.lastPtiDate);
      const lastStatus = row.lastPtiStatus
        ? getPtiStatusLabel(row.lastPtiStatus)
        : null;
      const lastPti = lastDate
        ? `Last PTI ${lastDate}${lastStatus ? ` · ${lastStatus}` : ""}`
        : "No PTI on file";
      return {
        id: `pti-${row.driverUserId}-${row.assetType}-${row.assetId}`,
        title: row.driverName,
        subtitle: `${row.assetDisplayLabel} · ${lastPti}`,
        meta: formatDue(row),
        tone: row.complianceStatus === "Overdue" ? "error" : "warning",
        onClick: () => navigate(buildPtiHref(row)),
      };
    });
  }, [navigate, ptiAttention?.items]);

  const maintenanceRows = useMemo<HubPreviewRow[]>(() => {
    const ordered = [...alerts].sort((a, b) => {
      if (a.severity === b.severity) return 0;
      return a.severity === "Critical" ? -1 : 1;
    });
    return ordered.map((alert) => ({
      id: `maint-${alert.id}`,
      title: alert.vehicleName,
      subtitle: alert.message || MAINTENANCE_ALERT_LABELS[alert.alertType] || alert.alertType,
      meta: alert.severity,
      tone: alert.severity === "Critical" ? "error" : "warning",
      onClick: () => navigate("/maintenance"),
    }));
  }, [alerts, navigate]);

  const faultRows = useMemo<HubPreviewRow[]>(() => {
    return [...faultsByVehicle.entries()].map(([vehicleId, vehicleFaults]) => {
      const primary =
        vehicleFaults.find((fault) => fault.stopLightOn) ??
        vehicleFaults.find((fault) => fault.checkEngineLightOn) ??
        vehicleFaults[0];
      const detail =
        primary?.dtcDescription ||
        primary?.spnDescription ||
        "Active fault";
      return {
        id: `fault-${vehicleId}`,
        title: primary?.vehicleName || `Vehicle #${vehicleId}`,
        subtitle: `${detail} · ${vehicleFaults.length} active`,
        meta: primary?.stopLightOn ? "Stop lamp" : primary?.severity || "Active",
        tone: primary?.stopLightOn ? "error" : "warning",
        onClick: () => navigate("/vehicles?tab=faults"),
      };
    });
  }, [faultsByVehicle, navigate]);

  const shopRows = useMemo<HubPreviewRow[]>(() => {
    return shopRequests.map((request) => {
      const requested = formatShortDate(request.requestedAt);
      const who = request.requestedByFullName || "Someone";
      return {
        id: `shop-${request.id}`,
        title: request.shopName || `Shop #${request.shopId}`,
        subtitle: `${who}${requested ? ` · ${requested}` : ""}`,
        meta: `→ ${getShopPreferenceLabel(request.targetPreference)}`,
        tone: "info",
        onClick: () => navigate("/shops?view=requests"),
      };
    });
  }, [navigate, shopRequests]);

  const badges: Record<string, number> = {};
  if (canPti) badges["/pti"] = overdueCount + dueSoonCount;
  if (canMaintenance) badges["/maintenance"] = alertsError ? 0 : alerts.length;
  if (canFaults) badges["/vehicles"] = faultsError ? 0 : faultsByVehicle.size;
  if (canShopRequests) badges["/shops"] = shopsError ? 0 : shopRequests.length;

  return (
    <HubOverviewSection shortcuts={shortcuts} badges={badges}>
        {canPti && (
          <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<PtiIcon />}
            title="Pre-trip inspections"
            href="/pti?tab=compliance&status=needsAttention"
            hrefLabel="Compliance"
            loading={ptiSummaryLoading || ptiListLoading}
            emptyText="No overdue or due-soon PTIs."
            counts={[
              { label: "overdue", value: overdueCount, tone: "error" },
              { label: "due soon", value: dueSoonCount, tone: "warning" },
            ]}
            rows={ptiRows}
          />
          </Grid>
        )}
        {canMaintenance && (
          <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<MaintenanceIcon />}
            title="Maintenance"
            href="/maintenance"
            hrefLabel="Alerts"
            loading={alertsLoading}
            emptyText="No active maintenance alerts."
            counts={[
              {
                label: "critical",
                value: alertsError ? 0 : criticalAlerts.length,
                tone: "error",
              },
              {
                label: "warning",
                value: alertsError ? 0 : warningAlerts.length,
                tone: "warning",
              },
            ]}
            rows={maintenanceRows}
          />
          </Grid>
        )}
        {canFaults && (
          <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<VehiclesIcon />}
            title="Vehicle faults"
            href="/vehicles?tab=faults"
            hrefLabel="Faults"
            loading={faultsLoading}
            emptyText="No active vehicle faults."
            counts={[
              {
                label: "units",
                value: faultsError ? 0 : faultsByVehicle.size,
                tone: "warning",
              },
              {
                label: "events",
                value: faultsError ? 0 : faults.length,
                tone: "neutral",
              },
            ]}
            rows={faultRows}
          />
          </Grid>
        )}
        {canShopRequests && (
          <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<StoreIcon />}
            title="Shop requests"
            href="/shops?view=requests"
            hrefLabel="Requests"
            loading={shopsLoading}
            emptyText="No pending DNU or preference requests."
            counts={[
              {
                label: "pending",
                value: shopsError ? 0 : shopRequests.length,
                tone: "info",
              },
            ]}
            rows={shopRows}
          />
          </Grid>
        )}
    </HubOverviewSection>
  );
};

export default FleetHubOverview;
